<template>
  <div>
    <!-- Content -->
    <div
      class="w-full h-full flex justify-center pt-0 p-3 mt-10 tablet-v:mt-[56px] desktop:mt-[126px]"
    >
      <div class="w-full max-w-[440px]">
        <!-- Header -->
        <div class="flex items-center justify-between mb-[50px]">
          <div
            class="font-primary font-medium text-[26px] leading-[20px] text-gray-800"
          >
            Reset password
          </div>
        </div>

        <!-- Form -->
        <div class="flex flex-col">
          <div
            class="font-primary text-[15px] leading-[20px] text-gray-800 mb-6"
          >
            Enter a new password for your account.
          </div>
          <div class="mb-[14px]">
            <UiInput
              v-model="newPassword"
              label="New password"
              type="password"
              :disabled="loading"
              :error-msg="newPasswordError"
              :tabindex="1"
            />
          </div>
          <div class="mb-5">
            <UiInput
              v-model="confirmPassword"
              label="Confirm password"
              type="password"
              :disabled="loading"
              :error-msg="confirmPasswordError"
              :tabindex="2"
              @submit="handleSubmit"
            />
          </div>
          <div class="w-full flex justify-end">
            <UiButton
              class="w-full! tablet-h:w-auto!"
              primary
              :disabled="disableButton"
              :loading="loading"
              @click="handleSubmit"
            >
              Submit
            </UiButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { UiInput, UiButton } from '@dacr/ui'
import {
  useResetPasswordMutation,
  useValidateResetPasswordTokenMutation,
} from '@/shared/graphql/legacy/generated'
import { useRoute, useRouter } from 'vue-router'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { PASSWORD_REGEX } from '@/shared/utils/string'

const { mutate: validateToken } = useValidateResetPasswordTokenMutation()
const { mutate: resetPassword } = useResetPasswordMutation()

const route = useRoute()
const router = useRouter()
const toastStore = useToastStore()

const token = route.query.token as string

const newPassword = ref('')
const confirmPassword = ref('')

const newPasswordError = ref('')
const confirmPasswordError = ref('')

const disableButton = computed(
  () => !newPassword.value.trim() || !confirmPassword.value.trim()
)

const loading = ref(false)

watch(newPassword, () => {
  newPasswordError.value = ''
})

watch(confirmPassword, () => {
  confirmPasswordError.value = ''
})

const validate = () => {
  let hasError = false

  if (!newPassword.value.trim()) {
    newPasswordError.value = 'Please enter a password'
    hasError = true
  }

  if (newPassword.value && !PASSWORD_REGEX.test(newPassword.value.trim())) {
    newPasswordError.value =
      'Password must be 8 characters or more including at least 1 number and 1 special character'
    hasError = true
  }

  if (!confirmPassword.value.trim()) {
    confirmPasswordError.value = 'Please enter a password.'
    hasError = true
  }

  if (
    confirmPassword.value &&
    newPassword.value.trim() !== confirmPassword.value.trim()
  ) {
    confirmPasswordError.value = 'Confirmation password does not match password'
    hasError = true
  }

  return !hasError
}

const handleSubmit = async () => {
  try {
    if (!validate()) return

    loading.value = true

    const validationResponse = await validateToken({
      token,
    })

    if (!validationResponse?.data?.validateResetPasswordToken) {
      return toastStore.showToast({
        type: ToastType.Error,
        content: 'Token is not valid or has expired.',
      })
    }

    await resetPassword({
      token,
      newPassword: newPassword.value,
    })

    newPassword.value = ''
    confirmPassword.value = ''

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Your password was successfully reset',
    })

    router.push('/signout')
  } catch (error) {
    if (error instanceof Error) {
      console.error(error)

      toastStore.showToast({
        type: ToastType.Error,
        content: 'Could not reset your password - please try again later',
        error,
      })
    }
  } finally {
    loading.value = false
  }
}
</script>
